import React, { useState, useEffect } from 'react';
import CompanyAdminPicker from '../components/CompanyAdminPicker';
import CompanyModulePicker from '../components/CompanyModulePicker';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';
const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';
// Country codes using clean text abbreviations
const COUNTRY_CODES = [
  { abbr: 'LB', code: '+961' }, { abbr: 'AE', code: '+971' }, { abbr: 'SA', code: '+966' },
  { abbr: 'QA', code: '+974' }, { abbr: 'KW', code: '+965' }, { abbr: 'BH', code: '+973' },
  { abbr: 'OM', code: '+968' }, { abbr: 'JO', code: '+962' }, { abbr: 'SY', code: '+963' },
  { abbr: 'IQ', code: '+964' }, { abbr: 'EG', code: '+20' },  { abbr: 'US', code: '+1' },
  { abbr: 'GB', code: '+44' },  { abbr: 'FR', code: '+33' },  { abbr: 'DE', code: '+49' },
  { abbr: 'IT', code: '+39' },  { abbr: 'ES', code: '+34' },  { abbr: 'TR', code: '+90' },
  { abbr: 'CY', code: '+357' }, { abbr: 'AU', code: '+61' },  { abbr: 'IN', code: '+91' },
  { abbr: 'CN', code: '+86' }
];

// Country to Governorate Mapping Filter
const COUNTRY_GOVERNORATE_MAP = {
  'Lebanon': ['Beirut', 'Tripoli', 'Sidon', 'Tyre', 'Zahle', 'Jounieh', 'Baalbek', 'Nabatieh', 'Aley'],
  'UAE': ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain'],
  'Saudi Arabia': ['Riyadh', 'Jeddah', 'Mecca', 'Medina', 'Dammam', 'Khobar', 'Tabuk'],
  'Qatar': ['Doha', 'Al Wakrah', 'Al Khor', 'Al Rayyan', 'Lusail'],
  'Kuwait': ['Kuwait City', 'Al Ahmadi', 'Hawalli', 'Salmiya', 'Al Jahra'],
  'Bahrain': ['Manama', 'Muharraq', 'Riffa', 'Hamad Town', 'Isa Town'],
  'Oman': ['Muscat', 'Salalah', 'Sohar', 'Nizwa', 'Sur'],
  'Jordan': ['Amman', 'Zarqa', 'Irbid', 'Aqaba', 'Jerash'],
  'Syria': ['Damascus', 'Aleppo', 'Homs', 'Latakia', 'Hama'],
  'Iraq': ['Baghdad', 'Basra', 'Erbil', 'Mosul', 'Najaf'],
  'Egypt': ['Cairo', 'Alexandria', 'Giza', 'Sharm El Sheikh', 'Luxor'],
  'USA': ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Miami'],
  'UK': ['London', 'Birmingham', 'Manchester', 'Glasgow', 'Liverpool'],
  'France': ['Paris', 'Marseille', 'Lyon', 'Toulouse', 'Nice'],
  'Germany': ['Berlin', 'Munich', 'Frankfurt', 'Hamburg', 'Cologne'],
  'Italy': ['Rome', 'Milan', 'Naples', 'Turin', 'Venice'],
  'Spain': ['Madrid', 'Barcelona', 'Valencia', 'Seville', 'Zaragoza'],
  'Turkey': ['Istanbul', 'Ankara', 'Izmir', 'Antalya', 'Bursa'],
  'Cyprus': ['Nicosia', 'Limassol', 'Larnaca', 'Paphos'],
  'Australia': ['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide'],
  'India': ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai'],
  'China': ['Beijing', 'Shanghai', 'Shenzhen', 'Guangzhou', 'Chengdu']
};

const COUNTRIES_LIST = Object.keys(COUNTRY_GOVERNORATE_MAP).sort();

function CompanyCreate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lookupError, setLookupError] = useState(null);

  // State for fetched users from database
  const [availableAdmins, setAvailableAdmins] = useState([]);

  // State for fetched real modules from database 
  const [availableModules, setAvailableModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(true);

  // Tracks if the user has typed anything (to trigger the unsaved warning)
  const [isDirty, setIsDirty] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    legal_name: '',
    countryCode: '+961',
    phone: '',
    country: '',
    governorate: '',
    admin_ids: [],
    is_private: false,
    module_ids: [],
    status: 'Pending Setup'
  });

  // Load protected lookup lists without allowing an error object into list state.
  useEffect(() => {
    const controller = new AbortController();
    setLoadingModules(true);

    const loadList = async (url, label, key) => {
      const response = await fetch(url, {
        headers: getAuthHeaders(),
        signal: controller.signal
      });
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Your session has expired. Please sign in again.');
        }
        throw new Error('Unable to load ' + label + '. Please refresh the page or contact your administrator.');
      }
      const data = await response.json();
      const items = Array.isArray(data) ? data : data?.[key];
      if (!Array.isArray(items)) {
        throw new Error('The server returned an invalid ' + label + ' list. Please refresh the page or contact your administrator.');
      }
      return items;
    };

    Promise.all([
      loadList(API + '/users', 'administrator', 'users'),
      loadList(API + '/modules', 'module', 'modules')
    ])
      .then(([admins, modules]) => {
        if (controller.signal.aborted) return;
        setAvailableAdmins(admins);
        setAvailableModules(modules);
        setLookupError(null);
      })
      .catch(err => {
        if (!controller.signal.aborted) {
          setLookupError(err.message || 'Unable to load company setup data. Please refresh the page.');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingModules(false);
      });

    return () => controller.abort();
  }, []);

  // Warn user if they try to refresh or close the browser tab with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const capitalizeWords = (str) => {
    return str.replace(/\b\w/g, char => char.toUpperCase());
  };

  const handleChange = (e) => {
    setIsDirty(true);
    const { name, value } = e.target;

    let finalValue = value;
    if (name === 'name') {
      finalValue = capitalizeWords(value);
    }

    if (name === 'country') {
      setFormData({ ...formData, country: finalValue, governorate: '' });
    } else {
      setFormData({ ...formData, [name]: finalValue });
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      const confirmDiscard = window.confirm("You have unsaved changes. Are you sure you want to discard them and leave?");
      if (!confirmDiscard) return;
    }
    navigate('/companies');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading || loadingModules || lookupError) return;
    if (!formData.admin_ids.length) { setError('Select at least one company administrator.'); return; }
    if (!formData.module_ids.length) { setError('Select at least one module.'); return; }
    setLoading(true);
    setError(null);

    const payload = {
      name: formData.name,
      code: (formData.code || '').trim() || null,
      legal_name: formData.legal_name,
      phone: `${formData.countryCode} ${formData.phone}`,
      location: `${formData.governorate}, ${formData.country}`,
      modules: formData.module_ids,
      status: formData.status,
      admin_ids: formData.admin_ids,
      is_private: formData.is_private
    };

    fetch(API + '/companies', {
      method: 'POST',
      headers: getAuthHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }),
      body: JSON.stringify(payload)
    })
      .then(async (res) => {
        if (res.status === 401 || res.status === 403) {
          navigate('/login');
          throw new Error('Not authorized - please log in with a Holding-level account.');
        }
        if (!res.ok) { const data = await res.json(); throw new Error(Object.values(data.errors || {}).flat().join(' ') || data.message || 'Unable to create company.'); }
        return res.json();
      })
      .then((data) => {
        setLoading(false);
        setIsDirty(false);
        navigate('/companies');
      })
      .catch((err) => {
        setLoading(false);
        setError(err.message);
      });
  };

  const availableGovernorates = formData.country ? COUNTRY_GOVERNORATE_MAP[formData.country].sort() : [];

  return (
    <div style={{ padding: '40px', maxWidth: '700px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0, color: '#0f172a' }}>Create New Company</h2>
        <button
          type="button"
          onClick={handleCancel}
          style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#fff', cursor: 'pointer', fontWeight: '600', color: '#64748b' }}
        >
          Discard & Cancel
        </button>
      </div>

      {(lookupError || error) && (
        <div role="alert" style={{ padding: '12px', backgroundColor: '#fee2e2', color: '#ef4444', borderRadius: '6px', marginBottom: '20px' }}>
          {lookupError || error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px -4px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>

        {/* TEXT FIELDS */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#334155', fontSize: '0.9rem' }}>Company Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              style={{ width: '100%', padding: '10px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', boxSizing: 'border-box' }}
              placeholder="E.g. Access Consultancy"
            />
          </div>

          <div>
            <label style={{ display:'block', marginBottom:8, fontWeight:600, color:'#334155', fontSize:'0.9rem' }}>Company Code</label>
            <input aria-label="Company Code" value={formData.code || ''} onChange={e => setFormData(p => ({...p, code:e.target.value.toUpperCase()}))} placeholder="e.g. ACA" maxLength={40} style={{width:'100%',boxSizing:'border-box',padding:12,border:'1px solid #cbd5e1',borderRadius:8,marginBottom:8}} />
            <small style={{display:'block',color:'#64748b',marginBottom:18}}>Used as the prefix for newly generated organizational codes.</small>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#334155', fontSize: '0.9rem' }}>Legal Company Name</label>
            <input
              type="text"
              name="legal_name"
              value={formData.legal_name}
              onChange={handleChange}
              style={{ width: '100%', padding: '10px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', boxSizing: 'border-box' }}
              placeholder="E.g. Access Consultancy Holdings LLC"
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#334155', fontSize: '0.9rem' }}>Phone Number</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <select
                name="countryCode"
                value={formData.countryCode}
                onChange={handleChange}
                style={{
                  width: '100px',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  outline: 'none',
                  backgroundColor: '#f8fafc',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontFamily: 'monospace'
                }}
              >
                {COUNTRY_CODES.map(country => (
                  <option key={country.abbr} value={country.code}>
                    {country.abbr + ' ' + country.code.padEnd(4, '\u00A0')}
                  </option>
                ))}
              </select>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                style={{ flex: 1, padding: '10px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', boxSizing: 'border-box' }}
                placeholder="71 123 456"
              />
            </div>
          </div>
        </div>

        {/* COUNTRY AND GOVERNORATE FILTER DROPDOWNS (together make up "Location") */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#334155', fontSize: '0.9rem' }}>Country</label>
            <select
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
              style={{ width: '100%', padding: '10px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', boxSizing: 'border-box', backgroundColor: '#fff', cursor: 'pointer' }}
            >
              <option value="" disabled>Select Country...</option>
              {COUNTRIES_LIST.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#334155', fontSize: '0.9rem' }}>Governorate</label>
            <select
              name="governorate"
              value={formData.governorate}
              onChange={handleChange}
              required
              disabled={!formData.country}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                outline: 'none',
                boxSizing: 'border-box',
                backgroundColor: formData.country ? '#fff' : '#f1f5f9',
                cursor: formData.country ? 'pointer' : 'not-allowed'
              }}
            >
              <option value="" disabled>
                {formData.country ? 'Select Governorate...' : 'Select a Country first'}
              </option>
              {availableGovernorates.map(gov => (
                <option key={gov} value={gov}>{gov}</option>
              ))}
            </select>
          </div>
        </div>

        <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '30px 0' }} />

        <CompanyAdminPicker users={availableAdmins} selected={formData.admin_ids} onChange={ids => { setIsDirty(true); setFormData(previous => ({ ...previous, admin_ids: ids })); }} />
        <div style={{ marginBottom: 24, padding: 16, border: '1px solid #cbd5e1', borderRadius: 8 }}>
          <label style={{ fontWeight: 600 }}><input type="checkbox" role="switch" checked={formData.is_private} onChange={e => { setIsDirty(true); setFormData({ ...formData, is_private: e.target.checked }); }} /> Private company</label>
          <p style={{ fontSize: 13, color: '#475569' }}>Exclude this company from holding dashboards and totals. Holding administrators and superadmins will have no access unless explicitly assigned as a company administrator.</p>
          <p style={{ fontSize: 13, color: '#475569' }}>Only an assigned company administrator can restore holding access in Roles &amp; Permissions.</p>
        </div>

        <CompanyModulePicker modules={availableModules} selected={formData.module_ids} onChange={selected => { setIsDirty(true); setFormData(previous => ({ ...previous, module_ids: selected })); }} />

        <button
          type="submit"
          disabled={loading || loadingModules || Boolean(lookupError)}
          style={{ width: '100%', padding: '14px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '1rem', transition: 'background 0.2s' }}
        >
          {loading ? 'Saving...' : 'Save & Create Company'}
        </button>

      </form>
    </div>
  );
}

export default CompanyCreate;
